import type { ElasticService } from '~/types/ElasticService'
import type { PublicService } from '~/types/PublicService'

/**
 *
 * To keep elastic quick, elastic services only have keys used for search, and any needed for direct front end display.
 * This function turns an elastic service into a public service type so that it can be used in the front end for components
 * that require both elastic and public service objects to be used.
 * Because of the discrepency in data, some keys will be blank as they don't exist in the elastic service.
 */
export const transformElasticServiceIntoPublicService = (elasticService: ElasticService): PublicService => {
  const physicalAddress = {
    address1: elasticService.address_line1,
    address2: elasticService.address_line2,
    address3: '', // not in elastic
    city: elasticService.address_town,
    county: elasticService.address_county,
    country: '', // not in elastic
    isVisibleToPublic: elasticService.address_isVisibleToPublic,
    postalCode: elasticService.address_postcode
  }
  const { isVisibleToPublic } = physicalAddress
  const { address1, address2, address3, city, country, county, postalCode } = physicalAddress
  const addressParts = [address1, address2, address3, city, county, country, postalCode]
  const adddressDescription = addressParts.filter((part) => part).join(', ')

  const publicService: PublicService = {
    uid: elasticService.uid,
    charityNumber: '',
    companyNumber: '',
    contacts: [],
    location: {
      physicalAddress: isVisibleToPublic ? physicalAddress : {},
      description: isVisibleToPublic ? adddressDescription : '',
      latitude: isVisibleToPublic ? elasticService.coordinates.lat : 0,
      longitude: isVisibleToPublic ? elasticService.coordinates.lon : 0,
      name: isVisibleToPublic ? adddressDescription : ''
    },
    coordinates: { lat: elasticService.coordinates.lat, lng: elasticService.coordinates.lon },
    streetViewConfig: {
      coordinates: { lat: elasticService.coordinates.lat, lng: elasticService.coordinates.lon },
      heading: 0, // not in elastic
      pitch: 0, // not in elastic
      zoom: 1 // not in elastic
    },
    catchmentArea: elasticService.catchmentArea,
    descriptionHtml: elasticService.descriptionHtml,
    offersWheelChairAccess: elasticService.offersWheelChairAccess,
    hasFreeParkingFacilities: elasticService.hasFreeParkingFacilities,
    hasParkingFacilities: elasticService.hasParkingFacilities,
    hasToilets: elasticService.hasToilets,
    hasVisualAids: elasticService.hasVisualAids,
    hasHearingAndAuditoryAids: elasticService.hasHearingAndAuditoryAids,
    hasTextRelay: elasticService.hasTextRelay,
    hasBSL: elasticService.hasBSL,
    offersLanguageOptions: elasticService.offersLanguageOptions,
    hasAccessibleWebsite: elasticService.hasAccessibleWebsite,
    offersWalkins: elasticService.offersWalkins,
    offersVirtualServices: elasticService.offersVirtualServices,
    offersInPersonSupport: elasticService.offersInPersonSupport,
    offersOnlineSupport: elasticService.offersOnlineSupport,
    offersTelephoneSupport: elasticService.offersTelephoneSupport,
    offersSmsSupport: elasticService.offersSmsSupport,
    offersPeerGroupSupport: elasticService.offersPeerGroupSupport,
    referralDescriptionHtml: elasticService.referralDescriptionHtml,
    helplineTel: elasticService.helplineTel,
    officeTelephoneNumber: '', // not in elastic
    internalCorrespondenceEmails: elasticService.internalCorrespondenceEmails,
    logoUrl: elasticService.logoUrl,
    maxAge: elasticService.maxAge,
    minAge: elasticService.minAge,
    name: elasticService.name,
    openingTimes: elasticService.openingTimes,
    socialLinks: {
      facebook: { isActive: false, url: '' }, // not in elastic
      instagram: { isActive: false, url: '' }, // not in elastic
      linkedin: { isActive: false, url: '' }, // not in elastic
      x: { isActive: false, url: '' } // not in elastic
    },
    supportNeedCategories: elasticService.supportNeedCategories,
    url: '', // not in elastic,
    updatedAt: elasticService.updatedAt || '',
    openingTimesType: elasticService.openingTimesType,
    completelyIrregularHoursText: elasticService.completelyIrregularHoursText,
    isVCSEProvider: elasticService.isVCSEProvider,
    isLocalAuthorityProvider: elasticService.isLocalAuthorityProvider,
    isNHSProvider: elasticService.isNHSProvider,
    isPrivateProvider: elasticService.isPrivateProvider,
    ageVerified: elasticService.ageVerified,
    forCarersAndFamilies: elasticService.forCarersAndFamilies,
    forFrontlineWorkers: elasticService.forFrontlineWorkers,
    forStudents: elasticService.forStudents,
    hasReferralProcess: elasticService.hasReferralProcess,
    involvesPhysicalActivityAndMovement: elasticService.involvesPhysicalActivityAndMovement,
    isEpilepsyFriendly: elasticService.isEpilepsyFriendly,
    isNationalProvider: elasticService.isNationalProvider,
    isPrisonAndProbationFriendly: elasticService.isPrisonAndProbationFriendly,
    isPrivateSectorFreeProvider: elasticService.isPrivateSectorFreeProvider,
    isPrivateSectorPaidProvider: elasticService.isPrivateSectorPaidProvider,
    offersEthnicSupport: elasticService.offersEthnicSupport,
    offersLgbtqSupport: elasticService.offersLgbtqSupport,
    languageOptionsTextHtml: elasticService.languageOptionsTextHtml,
    offersMensSupport: elasticService.offersMensSupport,
    offersTravellerSupport: elasticService.offersTravellerSupport,
    offersWomensSupport: elasticService.offersWomensSupport,
    open247: elasticService.open247,
    localAuthorityText: '', // not in elastic
    lastPublishedTimestamp: elasticService.lastPublishedTimestamp || ''
  }

  return publicService
}
